import './OnlinePurchase2RequestModal.css';

import React, { useState } from 'react';
import { Modal, Input, PhoneInput, Button, Typography } from '@vtblife/uikit';
import Icon from '@vtblife/uikit-icons';

import { useUserDetailsLoader } from '@search/auth/src/userDetails/userDetails';
import classname from '@search/classname/src';
import { formatPhone } from '@search/helpers/src/formatPhone';

import { CircleIconWrapper } from '../../CardAuthorBadge';
import { useClassifiedOfferDataLayerPush } from '../../../../../domain/offer/analytics/ClassifiedOfferGtmContext';
import { usePromoOnlinePurchase2EmailMutation } from './usePromoOnlinePurchase2EmailMutation';

const cn = classname.bind(null, 'OnlinePurchase2RequestModal');

export function OnlinePurchase2RequestModal({
    isMobile,
    onClose,
    offerUrl
}: {
    onClose?(): void;
    isMobile?: boolean;
    offerUrl?: string;
}) {
    const sendPromoOnlineEmail = usePromoOnlinePurchase2EmailMutation();

    const userInfoLoad = useUserDetailsLoader();
    const userInfo = userInfoLoad({});
    const person = userInfo.data?.userProfile.person;

    const [ name, setName ] = useState('');
    const [ phoneNumber, setPhoneNumber ] = useState('');
    const [ isPhoneTouched, setPhoneTouched ] = useState(false);
    const [ isSaving, setSaving ] = useState(false);
    const [ isSended, setIsSended ] = useState(false);

    React.useEffect(() => {
        if (person?.name) setName(person?.name ?? '');
        if (person?.primaryPhone?.formattedNumber) setPhoneNumber(person?.primaryPhone?.formattedNumber);
    }, [ person?.name, person?.primaryPhone ]);

    const isPhoneValid = phoneNumber.length > 10;
    const dataLayerPush = useClassifiedOfferDataLayerPush();

    const sendCallback = React.useCallback((e?: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLInputElement>) => {
        setPhoneTouched(true);
        setSaving(true);
        if (! isPhoneValid) return;

        // prevent submitted form updating page
        e?.preventDefault();

        dataLayerPush({
            event: 'up_action',
            theme: 'online_deal',
            type: 'request'
        });

        sendPromoOnlineEmail({
            name,
            phone: phoneNumber,
            offerLink: offerUrl ?? window.location.href,
            isRequest: true
        })
            .then(() => setIsSended(true))
            .finally(() => setSaving(false));
    }, [ isPhoneValid, name, phoneNumber, sendPromoOnlineEmail, dataLayerPush ]);

    const sendCallbackEnter = React.useCallback((e?: React.KeyboardEvent<HTMLInputElement>) => {
        if (e?.key !== 'Enter') return;
        sendCallback(e);
    }, [ sendCallback ]);

    const titleText = isSended ? 'Заявка принята' :
        'Покупка недвижимости онлайн';
    const subtitleText = isSended ?
        <>Скоро мы перезвоним вам по номеру <span style={{ whiteSpace: 'nowrap' }}>{formatPhone(phoneNumber, { skipParenthesis: true })}</span></> :
        'Быстрый и\u00A0безопасный способ покупки жилья без ипотеки';

    return (
        <Modal
            opened
            size={isSended ? 's' : 'm'}
            mobile={isMobile}
            swipeToClose={! isMobile}
            onClose={onClose}
            data-scroll-lock-ignore
        >
            {isMobile ? (
                <Modal.Header>{titleText}</Modal.Header>
            ) : (
                <Modal.Title>{titleText}</Modal.Title>
            )}
            <Typography variant='primary' className={cn('subtitle-gap', { isMobile, isSended })}>
                {subtitleText}
            </Typography>
            {isSended ? (
                <Button
                    fullWidth
                    variant='secondary'
                    onClick={onClose}
                >Закрыть</Button>
            ) : (<>
                <div className={cn('group')}>
                    <CheckItem title='Ускорим процесс и&nbsp;избавим от&nbsp;очередей'>достаточно всего одного визита в&nbsp;банк</CheckItem>
                    <CheckItem title='Подготовим и&nbsp;отправим документы в&nbsp;Росреестр'>с&nbsp;первого раза и&nbsp;без ошибок</CheckItem>
                    <CheckItem title='Сохраним деньги в&nbsp;безопасности'>продавец не&nbsp;получит оплату до&nbsp;регистрации сделки</CheckItem>
                </div>
                <form className={cn('group', { increasedMarginTop: true, isMobile })} onSubmit={sendCallback}>
                    <Input
                        dataTest='purchase-online-name'
                        label='Как вас зовут'
                        placeholder='Имя'
                        size='l'
                        onChange={setName}
                        onKeyDown={sendCallbackEnter}
                        value={name}
                        disabled={userInfo.loading}
                    />
                    <PhoneInput
                        dataTest='purchase-online-phone'
                        value={phoneNumber}
                        required
                        inputMode='numeric'
                        onChange={setPhoneNumber}
                        label='Номер телефона'
                        hint={isPhoneTouched && ! isPhoneValid ? 'Введите номер телефона' : ''}
                        size='l'
                        onBlur={e => setPhoneTouched(true)}
                        disabled={userInfo.loading}
                        onKeyDown={sendCallbackEnter}
                        error={isPhoneTouched && ! isPhoneValid}
                    />
                    <Button
                        fullWidth
                        type='submit'
                        variant='brand-purple'
                        disabled={userInfo.loading || isSaving}
                    >Подать заявку</Button>
                </form>
            </>)}
        </Modal>
    );
}

function CheckItem({
    icon = <CircleIconWrapper><Icon size='m' name='check' /></CircleIconWrapper>,
    title,
    children
}: {
    icon?: React.ReactNode;
    children: React.ReactNode;
    title: string;
}) {
    return (
        <div className={cn('check-item')}>
            {icon}
            <div className={cn('advantage')}>
                <Typography bold>{title}</Typography>
                <Typography variant='secondary-alone' color='secondary'>{children}</Typography>
            </div>
        </div>
    );
}
